// Pure id-remap logic for the "Copy Form" flow (z8nrz7bzar). Given a source
// FormDefinition, produces a save payload for a brand-new family: every
// section/field (and table row/column) gets a fresh id, and every intra-form
// reference to an old id is rewritten to point at the new one. No React/network
// imports, so this stays unit-testable like src/lib/scheduling/engine.ts.
//
// Fields/tables are built from NAMED properties, never `...spread` from a raw
// API object — the backend's CreateFormDefinitionDto whitelist rejects any
// unrecognized property with a 400 ("property X should not exist"), and a
// `GET /form-definitions/:id` response can carry properties (e.g. an
// unrelated Field Library-only prop that happens to ride along) that the
// create DTO never declared. getSections() already normalizes SECTIONS this
// way; this file does the same for fields/table configs, which getSections
// passes through unmodified.

import type {
  AttachmentConfig,
  FieldDefinition,
  FormDefinition,
  FormulaGate,
  SectionDefinition,
  TableCellConfig,
  TableColumn,
  TableConfig,
  TableRow,
  VisibilityRule,
} from '@/types/cgt'
import { remapFormulaTokens, suggestFieldId } from '@/lib/crossForm'
import { getSections } from '@/lib/formSections'
import { aggregateRefKey } from '@/lib/tableForm'
import { isLabelPosition, isLabelWidth, sanitizeFieldLayout } from '@/lib/fieldLayout'
import type { SaveDefinitionPayload } from '@/api/formsApi'

type IdMap = Map<string, string>

// The token rewriter now lives in `crossForm.ts`, shared with the spreadsheet
// importer (`formExcel.ts`), which has exactly the same second-pass problem.
//
// What matters HERE and nowhere else: the `tokenMap` this file builds carries
// BOTH field-id entries and table `rowId__colId` composite entries in one
// namespace, so a gate/visibility formula that happens to reference a table
// cell key (same `{...}` grammar) still gets remapped. Without that, such a
// token would silently keep pointing at the source's row/col ids, and since
// gates fail CLOSED the copy would become permanently uncompletable with no
// visible cause.

function remapIdOrIds(
  value: string | string[] | undefined,
  idMap: IdMap,
): string[] | undefined {
  if (value == null) return undefined
  const arr = Array.isArray(value) ? value : [value]
  return arr.map(v => idMap.get(v) ?? v)
}

function remapVisibility(rule: VisibilityRule | undefined, fieldIdMap: IdMap, tokenMap: IdMap): VisibilityRule | undefined {
  if (!rule) return undefined
  return {
    logic: rule.logic,
    action: rule.action,
    require: rule.require,
    groups: rule.groups.map(g => ({
      logic: g.logic,
      conditions: g.conditions.map(c => ({
        sourceFieldId: c.sourceFieldId ? (fieldIdMap.get(c.sourceFieldId) ?? c.sourceFieldId) : c.sourceFieldId,
        operator: c.operator,
        value: c.value,
        formula: remapFormulaTokens(c.formula, tokenMap),
      })),
    })),
  }
}

function remapGate(gate: FormulaGate | undefined, tokenMap: IdMap): FormulaGate | undefined {
  if (!gate) return undefined
  return { formula: remapFormulaTokens(gate.formula, tokenMap) ?? gate.formula, message: gate.message }
}

function remapAttachment(attachment: AttachmentConfig | undefined): AttachmentConfig | undefined {
  if (!attachment) return undefined
  return { enabled: attachment.enabled, required: attachment.required }
}

function mintTableIdMaps(config: TableConfig): { rowIdMap: IdMap; colIdMap: IdMap } {
  return {
    rowIdMap: new Map(config.rows.map(r => [r.id, crypto.randomUUID()])),
    colIdMap: new Map(config.columns.map(c => [c.id, crypto.randomUUID()])),
  }
}

/** Applies pre-minted row/col id maps to a table: rewrites `columns[].id`,
 *  `rows[].id`, the `cells` record's `${rowId}__${colId}` keys, and each
 *  cell's `{rowId__colId}` formula via the shared token map (so a cell
 *  formula referencing another cell in the SAME table resolves too).
 *  Built from named properties (TableConfigDto's whitelist), not a spread. */
function remapTableConfig(config: TableConfig, rowIdMap: IdMap, colIdMap: IdMap, tokenMap: IdMap): TableConfig {
  const cells: Record<string, TableCellConfig> = {}
  for (const [key, cell] of Object.entries(config.cells ?? {})) {
    const sep = key.indexOf('__')
    if (sep === -1) continue
    const newRowId = rowIdMap.get(key.slice(0, sep)) ?? key.slice(0, sep)
    const newColId = colIdMap.get(key.slice(sep + 2)) ?? key.slice(sep + 2)
    // Table cells are a `Record<string, unknown>` on the backend DTO (no
    // nested-class validation), so a full spread here carries no 400 risk —
    // unlike fields/columns/rows, which ARE validated against a class.
    cells[`${newRowId}__${newColId}`] = { ...cell, formula: remapFormulaTokens(cell.formula, tokenMap) }
  }

  const columns: TableColumn[] = config.columns.map(c => ({
    id: colIdMap.get(c.id)!,
    label: c.label,
    columnType: c.columnType,
    options: c.options,
    oos: c.oos,
    lisStage: c.lisStage,
    lisTestCode: c.lisTestCode,
  }))
  const rows: TableRow[] = config.rows.map(r => ({
    id: rowIdMap.get(r.id)!,
    label: r.label,
  }))

  return {
    rowHeaderLabel: config.rowHeaderLabel,
    columns,
    rows,
    allowAddRows: config.allowAddRows,
    showRowHeader: config.showRowHeader,
    showColumnHeader: config.showColumnHeader,
    cells: Object.keys(cells).length ? cells : undefined,
  }
}

/** Builds a copy's field from NAMED properties matching FieldDefinitionDto's
 *  whitelist — see the file header for why this can't be a `...field` spread. */
function remapField(
  field: FieldDefinition,
  fieldIdMap: IdMap,
  tokenMap: IdMap,
  tableIdMapsByFieldId: Map<string, { rowIdMap: IdMap; colIdMap: IdMap }>,
): FieldDefinition {
  const tableMaps = tableIdMapsByFieldId.get(field.id)
  const tableConfig = field.tableConfig && tableMaps
    ? remapTableConfig(field.tableConfig, tableMaps.rowIdMap, tableMaps.colIdMap, tokenMap)
    : undefined

  return {
    id: fieldIdMap.get(field.id)!,
    label: field.label,
    fieldType: field.fieldType,
    required: field.required,
    order: field.order,
    helpText: field.helpText,
    // A text block's whole content — dropping it copied an empty block.
    constantText: field.constantText,
    colSpan: field.colSpan,
    fieldWidth: field.fieldWidth,
    // Valid values only: the DTO 400s on anything else (z8nrz7djyx).
    labelPosition: isLabelPosition(field.labelPosition) ? field.labelPosition : undefined,
    labelWidth: isLabelWidth(field.labelWidth) ? field.labelWidth : undefined,
    options: field.options,
    formula: remapFormulaTokens(field.formula, tokenMap),
    formulaRefs: field.formulaRefs?.map(r => fieldIdMap.get(r) ?? r),
    displayFormat: field.displayFormat,
    requiredGroup: field.requiredGroup,
    // Group ids are tenant-owned and a copy stays in the same tenant, so they
    // carry over unchanged (z8nrz7dt45 AC5).
    allowedSignerGroupIds: field.allowedSignerGroupIds,
    distinctSignerSet: field.distinctSignerSet,
    isGate: field.isGate,
    gatedFormType: field.gatedFormType,
    anchored: field.anchored,
    // Stage names are tenant-owned and a copy stays in the same tenant.
    lisStage: field.lisStage,
    lisTestCode: field.lisTestCode,
    canChange: field.canChange,
    attachment: remapAttachment(field.attachment),
    isMrField: field.isMrField,
    mrFieldKey: field.mrFieldKey,
    tableConfig,
    // libraryFieldId is intentionally left as-is — it points at the Field
    // Library, not at a field on this form.
    libraryFieldId: field.libraryFieldId,
    optionSetId: field.optionSetId,
    unlockedBy: remapIdOrIds(field.unlockedBy, fieldIdMap),
    visibility: remapVisibility(field.visibility, fieldIdMap, tokenMap),
    unlockGate: remapGate(field.unlockGate, tokenMap),
    signatureGate: remapGate(field.signatureGate, tokenMap),
    // Plain numbers and group ids; a copy stays in the same tenant.
    oos: field.oos,
  }
}

/**
 * What a copy source has to provide. A full `FormDefinition` satisfies it, so
 * the Copy Form flow passes one unchanged — but the JSON importer
 * (`src/lib/formTransfer.ts`) has no row behind its form and would otherwise
 * have to fabricate `id`/`version`/`createdAt` just to call this. Only the two
 * properties that are actually required to build a payload are required here.
 */
export type FormCopySource = Pick<FormDefinition, 'category' | 'sections'> &
  Partial<Omit<FormDefinition, 'category' | 'sections'>>

/**
 * Builds the POST /form-definitions payload for a copy of `source` named
 * `name`. Always starts a new family: only named fields are copied through
 * (never `...source`), so `familyId`/`version`/`versionName`/
 * `lastVersionUpdateAt`/`latestActiveVersion`/`isTestData`/`active` never leak
 * from the source or get sent at all — `active` in particular isn't even a
 * property CreateFormDefinitionDto accepts (the backend always creates a new
 * form active). `handle` is omitted so the backend derives a fresh one from
 * `name` — the same contract every other new-form save already relies on
 * (see FormDefinitionPanel.handleSave).
 */
export function buildFormCopyPayload(source: FormCopySource, name: string): SaveDefinitionPayload {
  // `source` is an API response, which may carry sections under `fields`
  // (the backend's JSONB column name) or as a legacy flat field list —
  // getSections() is the app's one normalizer for that (see FormEditorPage's
  // getDefinition -> getSections call). It also rebuilds each section from
  // named properties, so sections are already whitelist-safe; fields are not
  // (see remapField).
  const sourceSections = getSections(source)

  const fieldIdMap: IdMap = new Map()
  const sectionIdMap: IdMap = new Map()
  const usedFieldIds = new Set<string>()
  const tableIdMapsByFieldId = new Map<string, { rowIdMap: IdMap; colIdMap: IdMap }>()
  // Shared token namespace for formula-style {…} rewriting: field ids, table
  // `rowId__colId` composite keys and table total tokens, so any {…} token,
  // wherever it's hand-typed, resolves through one map.
  const tokenMap: IdMap = new Map()

  for (const section of sourceSections) {
    sectionIdMap.set(section.id, crypto.randomUUID())
    for (const field of section.fields) {
      const newFieldId = suggestFieldId(field.label, usedFieldIds)
      fieldIdMap.set(field.id, newFieldId)
      tokenMap.set(field.id, newFieldId)
      if (field.tableConfig) {
        const tableMaps = mintTableIdMaps(field.tableConfig)
        tableIdMapsByFieldId.set(field.id, tableMaps)
        for (const [oldRowId, newRowId] of tableMaps.rowIdMap) {
          for (const [oldColId, newColId] of tableMaps.colIdMap) {
            tokenMap.set(`${oldRowId}__${oldColId}`, `${newRowId}__${newColId}`)
          }
        }
        // Column and row totals of this table (see lib/tableForm.ts). The token
        // names the table field AND a column or row, so both are remapped, as
        // cgt-backend's copy-definition does.
        for (const op of ['sum', 'avg'] as const) {
          for (const [oldColId, newColId] of tableMaps.colIdMap) {
            tokenMap.set(
              aggregateRefKey(field.id, 'col', oldColId, op),
              aggregateRefKey(newFieldId, 'col', newColId, op),
            )
          }
          for (const [oldRowId, newRowId] of tableMaps.rowIdMap) {
            tokenMap.set(
              aggregateRefKey(field.id, 'row', oldRowId, op),
              aggregateRefKey(newFieldId, 'row', newRowId, op),
            )
          }
        }
      }
    }
  }

  const sections: SectionDefinition[] = sourceSections.map(section => ({
    id: sectionIdMap.get(section.id)!,
    title: section.title,
    width: section.width,
    headerColor: section.headerColor,
    order: section.order,
    gatedByPrevious: section.gatedByPrevious,
    unlockedBySections: remapIdOrIds(section.unlockedBySections, sectionIdMap),
    visibility: remapVisibility(section.visibility, fieldIdMap, tokenMap),
    fields: section.fields.map(field => remapField(field, fieldIdMap, tokenMap, tableIdMapsByFieldId)),
  }))
  const fieldLayout = sanitizeFieldLayout(source.fieldLayout)

  return {
    name,
    category: source.category,
    formType: source.formType,
    sections,
    predecessorFormTypes: source.predecessorFormTypes,
    allowedActions: source.allowedActions,
    completionGate: remapGate(source.completionGate ?? undefined, tokenMap),
    completerGroupIds: source.completerGroupIds,
    // The form-wide label default. Sanitized because a JSON import carries it
    // from a file; omitted when there is none, as a fresh form would be.
    ...(fieldLayout ? { fieldLayout } : {}),
    // No `active` here: CreateFormDefinitionDto doesn't accept it (the global
    // ValidationPipe's forbidNonWhitelisted 400s on any unknown property) —
    // the backend's create() always makes a new form active by default,
    // regardless of the source's own status, which already satisfies the AC.
  }
}
